// Catalogue des modèles d'appareils de l'organisation active, chargé depuis l'API et partagé par
// tous les composants. Rechargé quand l'organisation change (changement d'organisation, mode support).
// Tant que l'API n'a pas répondu (ou si elle échoue), les modèles d'origine servent de secours :
// l'éditeur d'architecture reste utilisable.
import { useEffect, useState } from 'react';
import { deviceModelsApi, actingOrgId, type DeviceModel } from '../api/deviceModels';
import { setKnownDeviceModels } from '../i18n/defaultNames';
import { useAuth } from '../context/AuthContext';

const base = { orgId: null, scope: 'common' as const, active: true, formerNames: [], hidden: false, customized: false, baseName: null };
const FALLBACK: DeviceModel[] = [
  { ...base, id: -1, code: 'wattel7', name: 'Wattel 7', nameEn: null, nameHi: null, icon: '⚡', imageUrl: '/devices/wattel7.svg', category: 'charger', sortOrder: 10 },
  { ...base, id: -2, code: 'wattel22', name: 'Wattel 22', nameEn: null, nameHi: null, icon: '⚡', imageUrl: '/devices/wattel22.svg', category: 'charger', sortOrder: 20 },
  { ...base, id: -3, code: 'wattelgo', name: 'Wattel Go', nameEn: null, nameHi: null, icon: '🔌', imageUrl: '/devices/wattelgo.svg', category: 'socket', sortOrder: 30 },
  { ...base, id: -4, code: 'wattelwall', name: 'Wattel Wall', nameEn: null, nameHi: null, icon: '🔌', imageUrl: '/devices/wattelwall.svg', category: 'socket', sortOrder: 40 },
  { ...base, id: -5, code: 'autre', name: 'Autre', nameEn: 'Other', nameHi: 'अन्य', icon: '⚙️', imageUrl: null, category: 'other', sortOrder: 1000 },
];

let models: DeviceModel[] = FALLBACK;
let loadedFor: string | null = null;   // organisation du catalogue chargé
const listeners = new Set<(m: DeviceModel[]) => void>();
setKnownDeviceModels(models);

function publish(next: DeviceModel[]) {
  models = next;
  setKnownDeviceModels(next);
  listeners.forEach(l => l(next));
}

/** Recharge le catalogue (après une modification dans un écran de gestion). */
export function reloadDeviceModels(): Promise<void> {
  return deviceModelsApi.list()
    .then(list => publish(list.length ? list : FALLBACK))
    .catch(err => console.warn('[device-models] catalogue indisponible, modèles par défaut utilisés :', err?.message));
}

/** Modèles visibles pour l'organisation active, triés ; se met à jour quand le catalogue change. */
export function useDeviceModels(): DeviceModel[] {
  const { user } = useAuth();
  const [list, setList] = useState(models);
  const orgKey = `${user?.currentOrgId ?? ''}|${actingOrgId() ?? ''}`;

  useEffect(() => {
    listeners.add(setList);
    setList(models);
    return () => { listeners.delete(setList); };
  }, []);

  useEffect(() => {
    if (!user || loadedFor === orgKey) return;
    loadedFor = orgKey;
    reloadDeviceModels();
  }, [user, orgKey]);

  return list;
}
